import { DecimalSchema } from "../../decimal";
import { CAMPAIGN_VISIBILITY, REWARD_RULE } from "./hold-to-earn.enums";
import { z } from "zod";

/** classic: paid by the Cede vault. merkl: paid by a Merkl campaign. */
export const HteRewardDistributionSchema = z.enum(["classic", "merkl"]);
export type HteRewardDistribution = z.infer<typeof HteRewardDistributionSchema>;

// Basic campaign schema for API responses - matches what the live campaigns endpoint returns
export const HoldToEarnCampaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  shortDescription: z.string(),
  tokenSymbol: z.string(),
  startAt: z.coerce.date(),
  endAt: z.coerce.date(),
  rewardToken: z.string(),
  // Backwards compatibility fields - derived from epochs for frontend
  /**
   * Minimum USD value of tokens required for eligibility (derived from current/first epoch).
   * User's token balance is converted to USD and compared against this threshold.
   * @example 100 // User must hold at least $100 worth of tokens
   */
  minimumEligibleUsdBalance: z.number().positive().optional(), // From current/first epoch
  rewardRule: z.enum(REWARD_RULE).optional(), // From current/first epoch
  rewardPoolAmount: z.number().nonnegative().optional(), // From current/first epoch
  numberOfSpots: z.number().int().positive().optional(), // From current/first epoch
  /** Token amount held across all users that unlocks the full pool. Absent: the full pool is paid. */
  buyInAmountTarget: z.number().positive().optional(), // From current/first epoch
  exchangeIds: z.array(z.string()).optional(), // From current/first epoch (converted from EXCHANGES)
  epochDuration: z.number().positive().optional(), // From current/first epoch - duration in hours
  apr: z.number().nonnegative().optional(), // From current/first epoch
  isUpcoming: z.boolean().optional(), // Campaign hasn't started yet
  timeUntilStart: z.number().nonnegative().optional(), // Milliseconds until campaign starts (0 if not upcoming)
  production: z.boolean(),
  /**
   * Completion data for finished campaigns.
   * Contains historical max stats (APR, participants, total held amount) computed at campaign end.
   * Only present for campaigns that have ended.
   */
  completionData: z
    .object({
      lastEpochNumber: z.number().int().nonnegative(),
      completedAt: z.coerce.date(),
      /** Maximum APR achieved during the campaign (as string for decimal precision) */
      maxApr: z.string(),
      /** Maximum number of participants at any point during the campaign */
      maxParticipants: z.number().int().nonnegative(),
      /** Maximum total held amount (sum of all participants) achieved during the campaign (as string for decimal precision) */
      maxTotalHeldAmount: z.string(),
      /** Maximum total held amount in USD (based on token price snapshot at the time), defaults to "0" if price unavailable */
      maxTotalHeldAmountUsd: z.string().optional(),
    })
    .optional(),
  descriptions: z
    .array(
      z.object({
        title: z.string(),
        content: z.string(),
      }),
    )
    .optional(),
  epochs: z.array(
    z.object({
      epochNumber: z.number().int().nonnegative(),
      /**
       * @remarks
       * The active epoch id is the index of the epoch in the campaign when we don't consider the paused epochs
       */
      activeEpochId: z.number().int().nonnegative(),
      startDate: z.coerce.date(),
      endDate: z.coerce.date(),
      production: z.boolean(),
      /**
       * @remarks
       * Means the epoch is paused (partially or fully)
       * - Partially paused: the epoch started but was paused before it ended
       * - Fully paused: the epoch was paused before it started
       */
      isPaused: z.boolean().default(false),
      isPausedFrom: z.coerce.date().optional(),
      /**
       * @remarks
       * if the epoch is paused and the pause started before the current epoch start, then the epoch is considered fully paused
       * Otherwise, the epoch is considered partially paused (the pause started after the epoch start)
       */
      isFullyPaused: z.boolean().default(false),
      rewardPoolAmount: z.number().int().positive(),
      numberOfSpots: z.number().int().positive().optional(),
      /**
       * Minimum USD value of tokens required for eligibility.
       * User's token balance is converted to USD and compared against this threshold.
       * @example 100 // User must hold at least $100 worth of tokens
       */
      minimumEligibleUsdBalance: z.number().positive(),
      exchangeIds: z.array(z.string()),
      rewardRule: z.enum(REWARD_RULE),
    }),
  ),
  networkReward: z.array(z.string()),
  visibility: z.enum(CAMPAIGN_VISIBILITY),
  privateCampaignPartner: z.string().optional(),
  privateCampaignUtm: z.string().optional(),
  rewardVaultAddressProduction: z.string(),
  rewardVaultAddressDevelopment: z.string().optional(),
  rewardVaultAddressStaging: z.string().optional(),
  // Computed property - resolved vault address for the current environment
  rewardVaultAddress: z.string().optional(),
  uniqueParticipationByUser: z.boolean(),
  requireTelegramVerification: z.boolean(),
  requireDiscordVerification: z.boolean(),
  requireTwitterVerification: z.boolean(),
  icon: z.string().optional(),
  tokenIcon: z.string().optional(),
  campaignPartners: z.array(z.string()).optional(),
  // Optional for one release: a hub deployed ahead of the API must still parse campaigns without it.
  rewardDistribution: HteRewardDistributionSchema.optional(),
  /** The Merkl campaign paying a `merkl` campaign, once it exists. */
  merklCampaignId: z.string().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type HoldToEarnCampaign = z.infer<typeof HoldToEarnCampaignSchema>;

// Array schema for multiple campaigns
export const HoldToEarnCampaignsSchema = z.array(HoldToEarnCampaignSchema);
export type HoldToEarnCampaigns = z.infer<typeof HoldToEarnCampaignsSchema>;

// Extract the epoch type from the campaign schema
export const HoldToEarnCampaignEpochSchema = z.object({
  epochNumber: z.number().int().nonnegative(),
  /**
   * @remarks
   * The active epoch id is the index of the epoch in the campaign when we don't consider the paused epochs
   */
  activeEpochId: z.number().int().nonnegative(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  production: z.boolean(),
  /**
   * @remarks
   * Means the epoch is paused (partially or fully)
   * - Partially paused: the epoch started but was paused before it ended
   * - Fully paused: the epoch was paused before it started
   */
  isPaused: z.boolean().default(false),
  /**
   * @remarks
   * if the epoch is paused and the pause started before the current epoch start, then the epoch is considered fully paused
   * Otherwise, the epoch is considered partially paused (the pause started after the epoch start)
   */
  isFullyPaused: z.boolean().default(false),
  isPausedFrom: z.coerce.date().optional(),
  rewardPoolAmount: z.number().int().positive(),
  numberOfSpots: z.number().int().positive().optional(),
  /**
   * Minimum USD value of tokens required for eligibility.
   * User's token balance is converted to USD and compared against this threshold.
   * @example 100 // User must hold at least $100 worth of tokens
   */
  minimumEligibleUsdBalance: z.number().positive(),
  exchangeIds: z.array(z.string()),
  rewardRule: z.enum(REWARD_RULE),
});

export type HoldToEarnCampaignEpoch = z.infer<typeof HoldToEarnCampaignEpochSchema>;

// Join campaign parameter schema
export const JoinCampaignBodySchema = z.object({
  campaignId: z.string().min(1),
});

export type JoinCampaignBody = z.infer<typeof JoinCampaignBodySchema>;

// Join campaign response schema
export const JOIN_CAMPAIGN_ERROR_CODE = {
  CAMPAIGN_NOT_FOUND: "CAMPAIGN_NOT_FOUND",
  CAMPAIGN_NOT_LIVE: "CAMPAIGN_NOT_LIVE",
  NO_ACTIVE_EPOCH: "NO_ACTIVE_EPOCH",
  NO_SPOTS_AVAILABLE: "NO_SPOTS_AVAILABLE",
  NO_MATCHING_EXCHANGE_ACCOUNT: "NO_MATCHING_EXCHANGE_ACCOUNT",
  ALREADY_JOINED: "ALREADY_JOINED",
  EVM_WALLET_REQUIRED: "EVM_WALLET_REQUIRED",
} as const;
export const JOIN_CAMPAIGN_ERROR_CODE_SCHEMA = z.nativeEnum(JOIN_CAMPAIGN_ERROR_CODE);
export type JoinCampaignErrorCode = z.infer<typeof JOIN_CAMPAIGN_ERROR_CODE_SCHEMA>;

export const JoinCampaignResponseSchema = z.discriminatedUnion("success", [
  z.object({
    success: z.literal(true),
    // Optional human-readable message for UX; avoid relying on it programmatically
    message: z.string().optional(),
  }),
  z.object({
    success: z.literal(false),
    code: JOIN_CAMPAIGN_ERROR_CODE_SCHEMA,
    message: z.string(),
  }),
]);

export type JoinCampaignResponse = z.infer<typeof JoinCampaignResponseSchema>;

// Leaderboard SSE query parameter schema
export const LeaderboardSseSchema = z.object({
  campaignId: z.string().min(1),
  epochId: z.coerce.number().nonnegative(),
});

export type LeaderboardSseParams = z.infer<typeof LeaderboardSseSchema>;

// Get leaderboard endpoint parameter schema
export const GetLeaderboardParamsSchema = z.object({
  campaignId: z.string().min(1),
  epochId: z.coerce.number().nonnegative(),
});

export type GetLeaderboardParams = z.infer<typeof GetLeaderboardParamsSchema>;

// Leaderboard record schema
export const HteLeaderboardRecordSchema = z.object({
  id: z.uuid(),
  hubUserId: z.number().int().positive(),
  defiAddress: z.string().optional(),
  rank: z.number().int().min(0),
  tokenAmountAverage: z.string(), // Stored as string to handle decimal precision
  tokenAmount: z.string(), // Stored as string to handle decimal precision
  tokenAmountUsd: z.string().nullable().optional(), // Token amount in USD (tokenAmount * tokenPrice), null when price unavailable
  tokenSymbol: z.string().min(1),
  rewardTokenAmount: z.string(), // Stored as string to handle decimal precision
  rewardTokenSymbol: z.string().min(1),
  rewardTokenAddress: z.string().min(1),
  hteLeaderboardId: z.uuid(),
  rewardClaimed: z.boolean(),
  rewardClaimedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  isTopHolder: z.boolean(),
  isAmbassador: z.boolean().optional().default(false),
});

export type HteLeaderboardRecord = z.infer<typeof HteLeaderboardRecordSchema>;

// Get leaderboard response schema (array of records)
export const GetLeaderboardResponseSchema = z.array(HteLeaderboardRecordSchema);

export type GetLeaderboardResponse = z.infer<typeof GetLeaderboardResponseSchema>;

/** SSE `hte.leaderboard.computed`: the leaderboard was recomputed; clients refetch it. */
export const HteLeaderboardComputedEventSchema = z.object({
  campaignId: z.string(),
  epochNumber: z.number().int().nonnegative(),
  computedAt: z.coerce.date(),
});
export type HteLeaderboardComputedEvent = z.infer<typeof HteLeaderboardComputedEventSchema>;

// Get participant status endpoint parameter schema
export const GetParticipantStatusParamsSchema = z.object({
  campaignId: z.string().min(1),
});

export type GetParticipantStatusParams = z.infer<typeof GetParticipantStatusParamsSchema>;

// Participant status schema
export const HteParticipantStatusSchema = z.object({
  id: z.uuid(),
  campaignId: z.string().min(1),
  hubUserId: z.number().int().positive(),
  optedOut: z.boolean(),
  optedOutAtEpoch: z.number().int().nullable(),
});

export type HteParticipantStatus = z.infer<typeof HteParticipantStatusSchema>;

// Get participant status response schema (participant or null)
export const GetParticipantStatusResponseSchema = z.union([HteParticipantStatusSchema, z.null()]);

export type GetParticipantStatusResponse = z.infer<typeof GetParticipantStatusResponseSchema>;

// Get active participants endpoint parameter schema
export const GetActiveParticipantsParamsSchema = z.object({
  campaignId: z.string().min(1),
});

export type GetActiveParticipantsParams = z.infer<typeof GetActiveParticipantsParamsSchema>;

// Get active participants response schema (array of participants)
export const GetActiveParticipantsResponseSchema = z.array(HteParticipantStatusSchema);

export type GetActiveParticipantsResponse = z.infer<typeof GetActiveParticipantsResponseSchema>;

// Get campaign by ID parameter schema
export const GetCampaignByIdParamsSchema = z.object({
  campaignId: z.string().min(1),
});

export type GetCampaignByIdParams = z.infer<typeof GetCampaignByIdParamsSchema>;

// Get campaign by name parameter schema
export const GetCampaignByNameParamsSchema = z.object({
  campaignName: z.string().min(1, "Campaign name is required"),
});

export type GetCampaignByNameParams = z.infer<typeof GetCampaignByNameParamsSchema>;

// Participant status enum
export const PARTICIPANT_STATUS = {
  NOT_PARTICIPATING: "not-participating",
  PARTICIPATING: "participating",
  HOLDING: "holding",
  OPTED_OUT: "opted-out",
} as const;

export const ParticipantStatusSchema = z.nativeEnum(PARTICIPANT_STATUS);
export type ParticipantStatus = z.infer<typeof ParticipantStatusSchema>;

// Enhanced leaderboard record with participant status
export const EnhancedLeaderboardRecordSchema = HteLeaderboardRecordSchema.extend({
  participantStatus: ParticipantStatusSchema,
});

export type EnhancedLeaderboardRecord = z.infer<typeof EnhancedLeaderboardRecordSchema>;

// Get epoch statistics parameter schema
export const GetEpochStatisticsParamsSchema = z.object({
  campaignId: z.string().min(1),
  epochId: z.coerce.number().int().nonnegative(),
});

export type GetEpochStatisticsParams = z.infer<typeof GetEpochStatisticsParamsSchema>;

// Epoch statistics schema
export const EpochStatisticsSchema = z.object({
  totalParticipants: z.number().int().nonnegative(),
  activeParticipants: z.number().int().nonnegative(),
  holdingParticipants: z.number().int().nonnegative(),
  optedOutParticipants: z.number().int().nonnegative(),
  totalVolume: z.string(), // Decimal as string for precision
});

export type EpochStatistics = z.infer<typeof EpochStatisticsSchema>;

// Decimals as strings, in campaign token units
export const EpochPoolUnlockSchema = z.object({
  buyInAmountTarget: z.string(),
  /** Sum of every participant's latest snapshot amount. */
  heldTokenAmount: z.string(),
  /** min(1, heldTokenAmount / buyInAmountTarget) */
  unlockedRatio: z.string(),
});

export type EpochPoolUnlock = z.infer<typeof EpochPoolUnlockSchema>;

// Get epoch statistics response schema
export const GetEpochStatisticsResponseSchema = z.object({
  campaignId: z.string(),
  epochId: z.number().int().nonnegative(),
  statistics: EpochStatisticsSchema,
  /** Null until the hourly job has recomputed this epoch's leaderboard once. */
  // nullish while an older API (no field) can still serve a newer hub
  leaderboardLastComputedAt: z.coerce.date().nullish(),
  /** Null when the epoch pays its full pool whatever is held (no buy-in amount target). */
  // nullish while an older API (no field) can still serve a newer hub
  poolUnlock: EpochPoolUnlockSchema.nullish(),
  leaderboard: z.array(EnhancedLeaderboardRecordSchema),
});

export type GetEpochStatisticsResponse = z.infer<typeof GetEpochStatisticsResponseSchema>;

/**
 * DTO for claimable HTE reward records returned by HTE in-proc
 * Pre-mapped with all campaign and epoch metadata to avoid data wrangling in consumers
 * Used by getClaimableRecordsForUser and getClaimableSoonRecordsForUser
 */
export const HteClaimableRecordSchema = z.object({
  hubUserId: z.number().int().positive(),
  rank: z.number().int().min(0),
  rewardTokenAmount: z.string(), // Decimal as string for precision
  rewardTokenSymbol: z.string().min(1),
  rewardTokenAddress: z.string().min(1),
  rewardClaimed: z.boolean(),
  rewardClaimedAt: z.coerce.date().nullable(),
  hubUserDefiAddress: z.string(),
  campaignId: z.string(),
  epochId: z.number().int().nonnegative(),
  rewardVaultAddress: z.string(),
  campaignType: z.literal("HTE"),
  campaignData: z.object({
    id: z.string(),
    name: z.string(),
    icon: z.string().nullable(),
    title: z.string(),
  }),
  epochStart: z.coerce.date(),
  epochEnd: z.coerce.date(),
  activeEpochId: z.number().int().nonnegative(),
  cooldownEnd: z.coerce.date().nullable(),
});

export type HteClaimableRecord = z.infer<typeof HteClaimableRecordSchema>;

/**
 * DTO for individual HTE leaderboard record within finished leaderboard
 * Used for on-chain reward publication
 */
export const HteLeaderboardRecordForRewardsSchema = z.object({
  hubUserId: z.number().int().positive(),
  rank: z.number().int().min(0),
  rewardTokenAmount: z.string(), // Decimal as string for precision
  rewardTokenSymbol: z.string().min(1),
  rewardTokenAddress: z.string().min(1),
  rewardClaimed: z.boolean(),
  rewardClaimedAt: z.coerce.date().nullable(),
  hubUserDefiAddress: z.string(),
  campaignId: z.string(),
  epochId: z.number().int().nonnegative(),
  rewardVaultAddress: z.string(),
  cooldownEnd: z.coerce.date().nullable(),
});

export type HteLeaderboardRecordForRewards = z.infer<typeof HteLeaderboardRecordForRewardsSchema>;

/**
 * DTO for finished HTE leaderboard with all records
 * Returned by getFinishedLeaderboardByCampaignAndEpoch for on-chain reward publication
 */
export const HteFinishedLeaderboardSchema = z.object({
  campaignId: z.string(),
  epochId: z.number().int().nonnegative(),
  rewardVaultAddress: z.string(),
  cooldownEnd: z.coerce.date().nullable(),
  records: z.array(HteLeaderboardRecordForRewardsSchema),
});

export type HteFinishedLeaderboard = z.infer<typeof HteFinishedLeaderboardSchema>;

/**
 * DTO for HTE campaign with finished epochs
 * Used by listAllCampaignsWithFinishedEpochs for reward processing orchestration
 */
export const HteCampaignWithFinishedEpochsSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
  rewardVaultAddress: z.string(),
  rewardDistribution: HteRewardDistributionSchema,
  icon: z.string().nullable(),
  title: z.string(),
  epochs: z.array(
    z.object({
      id: z.number().int().nonnegative(),
      isPaused: z.boolean(),
      isFullyPaused: z.boolean().optional(),
      rewardTokenAddress: z.string(),
      startDate: z.coerce.date(),
      endDate: z.coerce.date(),
      activeEpochId: z.number().int().nonnegative(),
    }),
  ),
});

export type HteCampaignWithFinishedEpochs = z.infer<typeof HteCampaignWithFinishedEpochsSchema>;

/**
 * DTO for validating defi address specific criteria for a hold to earn campaign
 */
export const validateDefiAddressSchema = z.object({
  campaignName: z.string().min(1),
});

export type ValidateDefiAddress = z.infer<typeof validateDefiAddressSchema>;

export const HteLeaderboardRecordWithDecimalsSchema = z.object({
  id: z.uuid(),
  hubUserId: z.number().int().positive(),
  defiAddress: z.string().optional(),
  rank: z.number().int().min(0),
  tokenAmountAverage: DecimalSchema,
  tokenAmount: DecimalSchema,
  tokenSymbol: z.string().min(1),
  rewardTokenAmount: DecimalSchema,
  rewardTokenSymbol: z.string().min(1),
  rewardTokenAddress: z.string().min(1),
  hteLeaderboardId: z.uuid(),
  rewardClaimed: z.boolean(),
  rewardClaimedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type HteLeaderboardRecordWithDecimals = z.infer<typeof HteLeaderboardRecordWithDecimalsSchema>;

export const HteLeaderboardWithRecordsSchema = z.object({
  id: z.uuid(),
  campaignId: z.string().min(1),
  epochNumber: z.number().int().nonnegative(),
  rewardVaultAddress: z.string().min(1),
  cooldownEnd: z.coerce.date().nullable(),
  hteLeaderboardRecords: z.array(HteLeaderboardRecordWithDecimalsSchema).optional(),
});

export type HteLeaderboardWithRecords = z.infer<typeof HteLeaderboardWithRecordsSchema>;
